import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Application, Release, Product } from '../types';
import { DEFAULT_PRODUCTS, DEFAULT_APPLICATIONS } from '../defaults/defaultData';

export type ThemeMode = 'light' | 'dark';

interface AppContextType {
  products: Product[];
  applications: Application[];
  selectedProductId: string | null;
  selectedAppId: string | null;
  themeMode: ThemeMode;
  toggleTheme: () => void;
  addProduct: (product: Omit<Product, 'id' | 'createdAt'>) => void;
  updateProduct: (id: string, product: Partial<Product>) => void;
  deleteProduct: (id: string) => void;
  selectProduct: (id: string | null) => void;
  addApplication: (app: Omit<Application, 'id' | 'createdAt'> & { releases?: Release[] }) => void;
  updateApplication: (id: string, app: Partial<Application>) => void;
  deleteApplication: (id: string) => void;
  selectApplication: (id: string | null) => void;
  addRelease: (appId: string, release: Omit<Release, 'id'>) => void;
  updateRelease: (appId: string, releaseId: string, release: Partial<Release>) => void;
  deleteRelease: (appId: string, releaseId: string) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const STORAGE_KEY = 'wai-roadmap-data';
const THEME_STORAGE_KEY = 'wai-roadmap-theme';

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
  const [themeMode, setThemeMode] = useState<ThemeMode>('dark');

  // Load theme preference on mount
  useEffect(() => {
    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (storedTheme === 'dark' || storedTheme === 'light') {
      setThemeMode(storedTheme);
    } else {
      // Default to dark theme
      setThemeMode('dark');
    }
  }, []);

  // Save theme preference whenever it changes
  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, themeMode);
  }, [themeMode]);

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const data = JSON.parse(stored);
        const loadedProducts = data.products && data.products.length > 0 ? data.products : DEFAULT_PRODUCTS;
        const loadedApplications = data.applications && data.applications.length > 0 ? data.applications : DEFAULT_APPLICATIONS;
        setProducts(loadedProducts);
        setApplications(loadedApplications);
        
        // Auto-select first product if none selected
        const productId = data.selectedProductId || (loadedProducts.length > 0 ? loadedProducts[0].id : null);
        setSelectedProductId(productId);
        
        // Auto-select first application of the selected product if none selected
        let appId = data.selectedAppId;
        if (!appId && productId) {
          const filteredApps = loadedApplications.filter((app: Application) => app.productId === productId);
          appId = filteredApps.length > 0 ? filteredApps[0].id : null;
        }
        setSelectedAppId(appId);
      } catch (error) {
        console.error('Failed to load data from localStorage:', error);
        // Use defaults on error
        setProducts(DEFAULT_PRODUCTS);
        setApplications(DEFAULT_APPLICATIONS);
        // Auto-select first product and app
        if (DEFAULT_PRODUCTS.length > 0) {
          setSelectedProductId(DEFAULT_PRODUCTS[0].id);
          const defaultApps = DEFAULT_APPLICATIONS.filter((app: Application) => app.productId === DEFAULT_PRODUCTS[0].id);
          if (defaultApps.length > 0) {
            setSelectedAppId(defaultApps[0].id);
          }
        }
      }
    } else {
      // No stored data, use defaults
      setProducts(DEFAULT_PRODUCTS);
      setApplications(DEFAULT_APPLICATIONS);
      // Auto-select first product and app
      if (DEFAULT_PRODUCTS.length > 0) {
        setSelectedProductId(DEFAULT_PRODUCTS[0].id);
        const defaultApps = DEFAULT_APPLICATIONS.filter((app: Application) => app.productId === DEFAULT_PRODUCTS[0].id);
        if (defaultApps.length > 0) {
          setSelectedAppId(defaultApps[0].id);
        }
      }
    }
  }, []);

  // Save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      products,
      applications,
      selectedProductId,
      selectedAppId
    }));
  }, [products, applications, selectedProductId, selectedAppId]);

  const addProduct = (product: Omit<Product, 'id' | 'createdAt'>) => {
    const newProduct: Product = {
      ...product,
      id: Date.now().toString(),
      createdAt: new Date().toISOString()
    };
    setProducts([...products, newProduct]);
    setSelectedProductId(newProduct.id);
  };

  const updateProduct = (id: string, updates: Partial<Product>) => {
    setProducts(products.map(prod => 
      prod.id === id ? { ...prod, ...updates } : prod
    ));
  };

  const deleteProduct = (id: string) => {
    setProducts(products.filter(prod => prod.id !== id));
    // Also delete all applications under this product
    setApplications(applications.filter(app => app.productId !== id));
    if (selectedProductId === id) {
      setSelectedProductId(null);
      setSelectedAppId(null);
    }
  };

  const selectProduct = (id: string | null) => {
    setSelectedProductId(id);
    // Auto-select first application of the selected product
    if (id) {
      const filteredApps = applications.filter(app => app.productId === id);
      setSelectedAppId(filteredApps.length > 0 ? filteredApps[0].id : null);
    } else {
      setSelectedAppId(null);
    }
  };

  const addApplication = (app: Omit<Application, 'id' | 'createdAt'> & { releases?: Release[] }) => {
    const newApp: Application = {
      ...app,
      id: Date.now().toString(),
      releases: app.releases || [],
      createdAt: new Date().toISOString()
    };
    setApplications([...applications, newApp]);
    setSelectedAppId(newApp.id);
  };

  const updateApplication = (id: string, updates: Partial<Application>) => {
    setApplications(applications.map(app => 
      app.id === id ? { ...app, ...updates } : app
    ));
  };

  const deleteApplication = (id: string) => {
    setApplications(applications.filter(app => app.id !== id));
    if (selectedAppId === id) {
      setSelectedAppId(null);
    }
  };

  const selectApplication = (id: string | null) => {
    setSelectedAppId(id);
  };

  const addRelease = (appId: string, release: Omit<Release, 'id'>) => {
    const newRelease: Release = {
      ...release,
      id: Date.now().toString()
    };
    setApplications(applications.map(app => 
      app.id === appId 
        ? { ...app, releases: [...app.releases, newRelease] }
        : app
    ));
  };

  const updateRelease = (appId: string, releaseId: string, updates: Partial<Release>) => {
    setApplications(applications.map(app => 
      app.id === appId 
        ? {
            ...app,
            releases: app.releases.map(rel => 
              rel.id === releaseId ? { ...rel, ...updates } : rel
            )
          }
        : app
    ));
  };

  const deleteRelease = (appId: string, releaseId: string) => {
    setApplications(applications.map(app => 
      app.id === appId 
        ? { ...app, releases: app.releases.filter(rel => rel.id !== releaseId) }
        : app
    ));
  };

  const toggleTheme = () => {
    setThemeMode(prevMode => prevMode === 'light' ? 'dark' : 'light');
  };

  return (
    <AppContext.Provider value={{
      products,
      applications,
      selectedProductId,
      selectedAppId,
      themeMode,
      toggleTheme,
      addProduct,
      updateProduct,
      deleteProduct,
      selectProduct,
      addApplication,
      updateApplication,
      deleteApplication,
      selectApplication,
      addRelease,
      updateRelease,
      deleteRelease
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};
